import { useState, useRef, useEffect, ReactNode } from "react";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useSelector } from "react-redux";
import { selectOfertaInvalidandoOVenciendo } from "../../reduxSlices/globalFlagsSlice";

interface InvalidatingOfferLoaderProps {
  children: ReactNode;
}

function InvalidatingOfferLoader({ children }: InvalidatingOfferLoaderProps) {
  const isInvalidating = useSelector(selectOfertaInvalidandoOVenciendo);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const animationStartedRef = useRef(false);

  const clearCurrentInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const runCompleteProgress = () => {
    clearCurrentInterval();
    intervalRef.current = setInterval(() => {
      setProgressPercentage((prev) => {
        const next = Math.min(prev + 8, 100);
        if (next >= 100) {
          clearCurrentInterval();
          animationStartedRef.current = false;
          setTimeout(() => {
            setIsAnimating(false);
            setProgressPercentage(0);
          }, 400);
        }
        return next;
      });
    }, 30);
  };

  useEffect(() => {
    if (isInvalidating) {
      clearCurrentInterval();
      animationStartedRef.current = true;
      setIsAnimating(true);
      setProgressPercentage(0);

      const startTime = Date.now();
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / 30000) * 98, 98);
        setProgressPercentage(progress);

        if (progress >= 98) clearCurrentInterval();
      }, 100);
    } else if (animationStartedRef.current) {
      runCompleteProgress();
    }

    return clearCurrentInterval;
  }, [isInvalidating]);

  if (!isAnimating) {
    return <>{children}</>;
  }

  return (
    <div className="flex justify-center items-center h-full">
      <div className="flex flex-col items-center">
        <div style={{ width: 150, height: 150 }}>
          <CircularProgressbar
            value={progressPercentage}
            text={`${Math.round(progressPercentage)}%`}
            styles={buildStyles({
              rotation: -0.25,
              strokeLinecap: "round",
              textSize: "16px",
              pathTransitionDuration: 0.1,
              pathColor: "#00C4B3",
              textColor: "#1f2937",
              trailColor: "#ccebe8",
              backgroundColor: "#f3f4f6",
            })}
          />
        </div>
      </div>
    </div>
  );
}

export default InvalidatingOfferLoader;
